<script lang="ts">
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { Bell } from 'lucide-svelte';
  import { notificationsApi } from '$lib/api/notifications';
  import { getPublicKey, enablePush, pushSupported, currentSubscription } from '$lib/api/push';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import { route, routeFromPath } from '$lib/state/route.svelte';
  import { t } from '$lib/i18n';
  import type { AppNotification } from '$lib/types';
  import { notificationDisplayTitle } from '$lib/utils/systemLabels';

  const queryClient = useQueryClient();
  const query = createQuery(() => ({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(),
    refetchInterval: 60_000
  }));

  let open = $state(false);
  let showEnablePush = $state<boolean | null>(null);
  let pushBusy = $state(false);

  async function checkPushAvailability() {
    if (!pushSupported() || Notification.permission === 'denied') {
      showEnablePush = false;
      return;
    }
    const key = await getPublicKey();
    if (!key) {
      showEnablePush = false;
      return;
    }
    if (Notification.permission === 'granted') {
      showEnablePush = !(await currentSubscription());
    } else {
      showEnablePush = true;
    }
  }
  checkPushAvailability();

  function refresh() {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }

  function fmtRelative(iso: string): string {
    const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
    if (mins < 1) return t('notify.justNow');
    if (mins < 60) return t('notify.minutesAgo', { n: mins });
    const hours = Math.round(mins / 60);
    if (hours < 24) return t('notify.hoursAgo', { n: hours });
    return t('notify.daysAgo', { n: Math.round(hours / 24) });
  }

  async function openNotification(n: AppNotification) {
    open = false;
    if (!n.read_at) {
      notificationsApi
        .markRead([n.id])
        .then(refresh)
        .catch(() => {});
    }
    if (n.route) route.push(routeFromPath(n.route));
  }

  async function markAllRead() {
    try {
      await notificationsApi.markRead();
      refresh();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function handleEnablePush() {
    pushBusy = true;
    try {
      const result = await enablePush();
      if (result.ok) {
        showEnablePush = false;
        toast.success(t('notify.pushEnabled'));
      } else {
        toast.error(result.error || t('notify.pushFailed'));
      }
    } finally {
      pushBusy = false;
    }
  }
</script>

<div class="relative">
  <button
    type="button"
    class="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-navy hover:bg-toyama-light-carbon"
    aria-label={t('notify.title')}
    onclick={() => (open = !open)}
  >
    <Bell class="h-5 w-5" />
    {#if query.data && query.data.unread_count > 0}
      <span
        class="absolute right-1.5 top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
        style="background:#C8412B"
      >
        {query.data.unread_count > 9 ? '9+' : query.data.unread_count}
      </span>
    {/if}
  </button>

  {#if open}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div class="fixed inset-0 z-30" role="presentation" onclick={() => (open = false)}></div>

    <div
      class="tym-card absolute right-0 top-full z-40 mt-2 w-[340px] max-w-[calc(100vw-2rem)] overflow-hidden shadow-[0_12px_32px_rgba(16,34,58,.18)]"
    >
      <div class="flex items-center justify-between border-b border-toyama-cloudy-gray px-4 py-3">
        <span class="font-heading text-sm font-bold text-navy">{t('notify.title')}</span>
        <button type="button" class="text-xs font-semibold text-toyama-teal hover:underline" onclick={markAllRead}>
          {t('notify.markAllRead')}
        </button>
      </div>

      {#if showEnablePush}
        <div class="border-b border-toyama-cloudy-gray px-4 py-2.5">
          <button
            type="button"
            class="btn-secondary w-full !text-xs"
            disabled={pushBusy}
            onclick={handleEnablePush}
          >
            {pushBusy ? t('notify.enabling') : t('notify.enablePush')}
          </button>
        </div>
      {/if}

      <div class="max-h-[360px] overflow-y-auto">
        {#if query.isLoading}
          <div class="tym-loading-state p-4">{t('common.loading')}</div>
        {:else if query.data?.notifications.length}
          {#each query.data.notifications as n (n.id)}
            <button
              type="button"
              class="block w-full border-b border-[#f0f0f0] px-4 py-3 text-left last:border-none hover:bg-toyama-light-carbon {n.read_at
                ? ''
                : 'bg-toyama-lime/[.06]'}"
              onclick={() => openNotification(n)}
            >
              <div class="flex items-start justify-between gap-2">
                <p class="text-[13px] font-semibold text-navy">{notificationDisplayTitle(n)}</p>
                {#if !n.read_at}
                  <span class="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-toyama-lime"></span>
                {/if}
              </div>
              {#if n.body}
                <p class="mt-0.5 line-clamp-2 text-[12px] text-dark-gray">{n.body}</p>
              {/if}
              <p class="mt-1 text-[11px] text-dark-gray">{fmtRelative(n.created_at)}</p>
            </button>
          {/each}
        {:else}
          <div class="tym-empty-state p-4">{t('notify.empty')}</div>
        {/if}
      </div>
    </div>
  {/if}
</div>
